import enviro from 'enviro';
import { getIsQAProxy } from 'pricing-pages-lib/utils/proxyUtils';
import Raven from 'raven-js';
import { Component, isValidElement } from 'react';
import { NavMarker } from 'react-rhumb';
import { Metrics } from '../../../utils/metrics';
import { getContextFromCustomError } from './CustomErrors';
import { DefaultFallbackComponent } from './DefaultFallbackComponent';
import { ERROR_SEVERITY } from './ErrorSeverity';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const initialState = {
  hasError: false,
  error: null
};
/**
 * @see `ErrorBoundary.test.tsx` for usage examples.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = initialState;
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
  }
  resetErrorBoundary(...args) {
    const {
      error
    } = this.state;
    if (error !== null) {
      var _this$props$onReset, _this$props;
      (_this$props$onReset = (_this$props = this.props).onReset) === null || _this$props$onReset === void 0 || _this$props$onReset.call(_this$props, ...args);
    }
    this.setState(initialState);
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error, info) {
    var _this$props$onError, _this$props2;
    this.logSentry(error);
    this.logMetrics();
    (_this$props$onError = (_this$props2 = this.props).onError) === null || _this$props$onError === void 0 || _this$props$onError.call(_this$props2, error, info);
  }
  logSentry(error) {
    const {
      skipAutoSentryLogging,
      errorBoundaryName,
      sentryOptions
    } = this.props;

    // If prop is not provided, i.e. `undefined`, use severity to determine if logging should be skipped
    const skipLogging = skipAutoSentryLogging !== null && skipAutoSentryLogging !== void 0 ? skipAutoSentryLogging : this.props.severity === ERROR_SEVERITY.LOW;
    if (skipLogging) return;
    const {
      context,
      fingerprint
    } = getContextFromCustomError(error);
    Raven.captureException(error, Object.assign({}, sentryOptions, {
      extra: Object.assign({
        errorBoundaryName
      }, sentryOptions === null || sentryOptions === void 0 ? void 0 : sentryOptions.extra, context),
      fingerprint: fingerprint ? [fingerprint] : []
    }));
  }
  logMetrics() {
    const {
      skipAutoMetricsLogging,
      errorBoundaryName,
      metricsOptions
    } = this.props;

    // If prop is not provided, i.e. `undefined`, use severity to determine if logging should be skipped
    const skipLogging = skipAutoMetricsLogging !== null && skipAutoMetricsLogging !== void 0 ? skipAutoMetricsLogging : this.props.severity === ERROR_SEVERITY.LOW;
    if (skipLogging) return;
    Metrics.counter('error-boundary', Object.assign({
      errorBoundaryName: errorBoundaryName || ''
    }, metricsOptions)).increment();
  }
  render() {
    const {
      children,
      fallback,
      FallbackComponent,
      navMarkerName
    } = this.props;
    const {
      hasError,
      error
    } = this.state;
    let childToRender = children;
    if (hasError) {
      var _this$props$skipRhumb;
      const props = {
        error,
        resetErrorBoundary: this.resetErrorBoundary
      };
      if ( /*#__PURE__*/isValidElement(fallback) || fallback === null) {
        childToRender = fallback;
      } else if (FallbackComponent) {
        childToRender = /*#__PURE__*/_jsx(FallbackComponent, Object.assign({}, props));
      } else {
        if (enviro.isQa() || getIsQAProxy()) {
          console.error('ErrorBoundary requires either a fallback or FallbackComponent prop');
        }
        childToRender = /*#__PURE__*/_jsx(DefaultFallbackComponent, Object.assign({}, props));
      }

      // If prop is not provided, i.e. `undefined`, use severity to determine if Rhumb marker should be skipped
      const skipRhumb = (_this$props$skipRhumb = this.props.skipRhumb) !== null && _this$props$skipRhumb !== void 0 ? _this$props$skipRhumb : this.props.severity !== ERROR_SEVERITY.HIGH;
      return skipRhumb ? /*#__PURE__*/_jsx(_Fragment, {
        children: childToRender
      }) : /*#__PURE__*/_jsx(NavMarker, {
        name: navMarkerName || 'RHUMB_GLOBAL_ERROR',
        children: childToRender
      });
    }
    return childToRender;
  }
}
ErrorBoundary.defaultProps = {
  severity: ERROR_SEVERITY.MEDIUM
};